import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Settings, Users, Briefcase, DollarSign, ShieldCheck, Save,
  CheckCircle2, XCircle, RotateCcw, ChevronLeft, TrendingUp, Percent, Loader2,
  Wallet, Zap,
} from "lucide-react";
import PrivateLayout from "../components/PrivateLayout";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../services/api";

/**
 * AdminBusinessModel — Configuration Business V1 (Super Admin uniquement).
 * Additif : ne remplace aucun module existant.
 */
export default function AdminBusinessModel() {
  const { user } = useAuth();
  const isAdmin = ["admin", "super-admin"].includes(user?.role);
  const [config, setConfig] = useState(null);
  const [stats, setStats] = useState(null);
  const [services, setServices] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [saving, setSaving] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [c, s, sv, co] = await Promise.all([
        api.get("/v1/business/admin/config"),
        api.get("/v1/business/admin/stats"),
        api.get("/v1/business/admin/services", { params: { limit: 200 } }),
        api.get("/v1/business/admin/commissions", { params: { limit: 200 } }),
      ]);
      setConfig(c.data.config);
      setStats(s.data);
      setServices(sv.data.items || []);
      setCommissions(co.data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (isAdmin) loadAll(); }, [isAdmin, loadAll]);

  const patchConfig = async (partial) => {
    setSaving(true);
    try {
      const { data } = await api.patch("/v1/business/admin/config", partial);
      setConfig(data.config);
      toast.success("Configuration mise à jour");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const validateService = async (sid, action) => {
    try {
      await api.patch(`/v1/business/admin/services/${sid}/validate`, { action });
      toast.success(action === "validate" ? "Service validé" : action === "reject" ? "Service rejeté" : "Service remis en attente");
      loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!isAdmin) {
    return (
      <PrivateLayout title="Business Model">
        <div className="afr-empty" data-testid="business-forbidden">
          <div className="afr-empty__title">Accès restreint</div>
          <div className="afr-empty__lead">Réservé aux administrateurs.</div>
        </div>
      </PrivateLayout>
    );
  }

  return (
    <PrivateLayout
      title="💼 Business Model V1"
      breadcrumbs={[{ to: "/admin", label: "Admin" }, { label: "Business Model" }]}
      actions={<Link to="/admin" className="afr-btn afr-btn--outline afr-btn--sm"><ChevronLeft size={14} /> Admin</Link>}
    >
      <div style={{ display: "flex", gap: 6, marginBottom: 20, borderBottom: "1px solid var(--afr-border)", flexWrap: "wrap" }} data-testid="business-tabs">
        {[
          { k: "overview", label: "Vue d'ensemble", Icon: TrendingUp },
          { k: "config", label: "Configuration", Icon: Settings },
          { k: "services", label: `Services (${services.length})`, Icon: Briefcase },
          { k: "commissions", label: `Commissions (${commissions.length})`, Icon: Percent },
        ].map((t) => (
          <button
            key={t.k}
            type="button"
            onClick={() => setTab(t.k)}
            data-testid={`tab-${t.k}`}
            style={{
              padding: "10px 14px", border: 0, background: "transparent", cursor: "pointer",
              borderBottom: tab === t.k ? "3px solid var(--afr-primary)" : "3px solid transparent",
              color: tab === t.k ? "var(--afr-secondary)" : "#64748b",
              fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 6,
            }}
          >
            <t.Icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {loading && <div data-testid="business-loading">Chargement…</div>}

      {!loading && tab === "overview" && stats && (
        <OverviewTab stats={stats} config={config} />
      )}

      {!loading && tab === "config" && config && (
        <ConfigTab config={config} onSave={patchConfig} saving={saving} />
      )}

      {!loading && tab === "services" && (
        <ServicesTab services={services} onAction={validateService} />
      )}

      {!loading && tab === "commissions" && (
        <CommissionsTab commissions={commissions} config={config} onReload={loadAll} />
      )}
    </PrivateLayout>
  );
}

function OverviewTab({ stats, config }) {
  const cur = config?.employer_paid_offer_currency || "XOF";
  return (
    <div data-testid="tab-overview-content">
      <div className="afr-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12, marginBottom: 20 }}>
        <Kpi Icon={Users} label="Utilisateurs par rôle" value={sum(stats.users_by_role)} accent="#009640" />
        <Kpi Icon={Briefcase} label="Employeurs > quota" value={stats.employers_over_quota || 0} accent="#B45309" hint={`Limite ${stats.employer_free_offers_active} offres`} />
        <Kpi Icon={DollarSign} label="Volume brut missions" value={fmtMoney(stats.commissions?.total_gross, cur)} accent="#0F2A42" />
        <Kpi Icon={Percent} label="Commissions AFRICTAL" value={fmtMoney(stats.commissions?.total_commission, cur)} accent="#15803D" />
      </div>

      <div className="afr-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))" }}>
        <article className="afr-card" style={{ padding: 16 }} data-testid="overview-by-role">
          <h3 style={{ marginTop: 0, fontFamily: "'Outfit', sans-serif" }}>Utilisateurs par rôle</h3>
          <table className="afr-table" style={{ marginTop: 8 }}>
            <tbody>
              {Object.entries(stats.users_by_role || {}).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
                <tr key={k}><td style={{ textTransform: "capitalize" }}>{k}</td><td style={{ textAlign: "right", fontWeight: 700 }}>{v}</td></tr>
              ))}
            </tbody>
          </table>
        </article>

        <article className="afr-card" style={{ padding: 16 }} data-testid="overview-services">
          <h3 style={{ marginTop: 0, fontFamily: "'Outfit', sans-serif" }}>Services Freelance</h3>
          <table className="afr-table" style={{ marginTop: 8 }}>
            <tbody>
              {Object.entries(stats.services_by_status || {}).map(([k, v]) => (
                <tr key={k}><td style={{ textTransform: "capitalize" }}>{k}</td><td style={{ textAlign: "right", fontWeight: 700 }}>{v}</td></tr>
              ))}
              {Object.keys(stats.services_by_status || {}).length === 0 && (
                <tr><td colSpan="2" className="afr-muted">Aucun service publié pour le moment.</td></tr>
              )}
            </tbody>
          </table>
        </article>

        <article className="afr-card" style={{ padding: 16 }} data-testid="overview-providers">
          <h3 style={{ marginTop: 0, fontFamily: "'Outfit', sans-serif" }}>Fournisseurs de paiement</h3>
          <p className="afr-muted" style={{ fontSize: "0.86rem" }}>
            Tous les fournisseurs sont désactivés en V1. L'infrastructure est prête pour l'activation.
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {(config?.payment_providers || []).map((p) => (
              <span key={p.code} className="afr-badge" style={{
                background: p.enabled ? "rgba(21,128,61,0.12)" : "rgba(100,116,139,0.10)",
                color: p.enabled ? "#15803D" : "#64748b",
              }} data-testid={`provider-${p.code}`}>
                {p.enabled ? <CheckCircle2 size={11} /> : <XCircle size={11} />} {p.name}
              </span>
            ))}
          </div>
        </article>
      </div>
    </div>
  );
}

function ConfigTab({ config, onSave, saving }) {
  const [form, setForm] = useState({
    employer_free_offers_active: config.employer_free_offers_active,
    employer_paid_offer_price: config.employer_paid_offer_price,
    employer_paid_offer_currency: config.employer_paid_offer_currency,
    freelance_services_free_active: config.freelance_services_free_active,
    commission_rate_certified: config.commission_rate_certified,
    commission_rate_external: config.commission_rate_external,
    commissions_enabled: config.commissions_enabled,
    billing_enabled: config.billing_enabled,
  });
  const [orch, setOrch] = useState(config.orchestrator_quota_by_plan || {});

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setNum = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value === "" ? "" : Number(e.target.value) }));
  const setRate = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value === "" ? 0 : Number(e.target.value) / 100 }));
  const setChk = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.checked }));

  const save = (e) => {
    e.preventDefault();
    const payload = { ...form, orchestrator_quota_by_plan: { ...orch } };
    // Numeric normalization
    ["employer_free_offers_active", "employer_paid_offer_price", "freelance_services_free_active"].forEach((k) => {
      if (payload[k] === "") delete payload[k];
      else payload[k] = parseInt(payload[k], 10);
    });
    Object.keys(payload.orchestrator_quota_by_plan).forEach((k) => {
      payload.orchestrator_quota_by_plan[k] = parseInt(payload.orchestrator_quota_by_plan[k], 10) || 0;
    });
    onSave(payload);
  };

  return (
    <form onSubmit={save} data-testid="tab-config-content" style={{ maxWidth: 820 }}>
      <section className="afr-card" style={{ padding: 16, marginBottom: 16 }}>
        <h3 style={{ marginTop: 0 }}><Briefcase size={16} style={{ verticalAlign: "middle" }} /> Employeurs</h3>
        <div className="afr-grid" style={{ gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
          <Field label="Offres gratuites actives" testid="cfg-employer-limit">
            <input type="number" min="0" value={form.employer_free_offers_active} onChange={setNum("employer_free_offers_active")} />
          </Field>
          <Field label="Prix offre supp. (unité)" testid="cfg-offer-price">
            <input type="number" min="0" value={form.employer_paid_offer_price} onChange={setNum("employer_paid_offer_price")} />
          </Field>
          <Field label="Devise" testid="cfg-offer-currency">
            <input value={form.employer_paid_offer_currency} onChange={set("employer_paid_offer_currency")} />
          </Field>
        </div>
      </section>

      <section className="afr-card" style={{ padding: 16, marginBottom: 16 }}>
        <h3 style={{ marginTop: 0 }}><Users size={16} style={{ verticalAlign: "middle" }} /> Freelances & commissions</h3>
        <div className="afr-grid" style={{ gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
          <Field label="Services gratuits actifs" testid="cfg-services-limit">
            <input type="number" min="0" value={form.freelance_services_free_active} onChange={setNum("freelance_services_free_active")} />
          </Field>
          <Field label="Commission mission certifiée (%)" testid="cfg-rate-certified">
            <input type="number" min="0" max="100" step="0.5" value={(form.commission_rate_certified * 100).toFixed(2)} onChange={setRate("commission_rate_certified")} />
          </Field>
          <Field label="Commission mission externe (%)" testid="cfg-rate-external">
            <input type="number" min="0" max="100" step="0.5" value={(form.commission_rate_external * 100).toFixed(2)} onChange={setRate("commission_rate_external")} />
          </Field>
        </div>
        <label style={{ display: "flex", gap: 8, marginTop: 12, alignItems: "center" }}>
          <input type="checkbox" checked={form.commissions_enabled} onChange={setChk("commissions_enabled")} data-testid="cfg-commissions-enabled" />
          Module commissions activé
        </label>
      </section>

      <section className="afr-card" style={{ padding: 16, marginBottom: 16 }}>
        <h3 style={{ marginTop: 0 }}><Settings size={16} style={{ verticalAlign: "middle" }} /> Quotas Orchestrateur IA par plan</h3>
        <div className="afr-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12 }}>
          {Object.entries(orch).map(([plan, val]) => (
            <Field key={plan} label={plan} testid={`cfg-orch-${plan}`}>
              <input type="number" min="0" value={val} onChange={(e) => setOrch((o) => ({ ...o, [plan]: e.target.value }))} />
            </Field>
          ))}
        </div>
      </section>

      <section className="afr-card" style={{ padding: 16, marginBottom: 16 }}>
        <h3 style={{ marginTop: 0 }}><ShieldCheck size={16} style={{ verticalAlign: "middle" }} /> Facturation & paiements</h3>
        <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input type="checkbox" checked={form.billing_enabled} onChange={setChk("billing_enabled")} data-testid="cfg-billing-enabled" />
          Facturation active (nécessite Stripe / Flutterwave — pas encore branché)
        </label>
        <p className="afr-muted" style={{ fontSize: "0.84rem", marginTop: 8 }}>
          Aucun paiement réel n'est effectué en V1. Cette bascule reste désactivée jusqu'à l'intégration
          d'un fournisseur.
        </p>
      </section>

      <button type="submit" className="afr-btn afr-btn--primary" disabled={saving} data-testid="cfg-save-btn">
        {saving ? <Loader2 size={14} className="afr-spin" /> : <Save size={14} />} Enregistrer la configuration
      </button>
    </form>
  );
}

function ServicesTab({ services, onAction }) {
  return (
    <div data-testid="tab-services-content">
      {services.length === 0 && (
        <div className="afr-empty" data-testid="services-empty">
          <div className="afr-empty__title">Aucun service publié</div>
          <div className="afr-empty__lead">Les freelances publieront leurs services via /freelance/services.</div>
        </div>
      )}
      <div className="afr-grid" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 12 }}>
        {services.map((s) => (
          <article key={s.id} className="afr-card" data-testid={`service-${s.id}`} style={{ padding: 14, borderLeft: `4px solid ${statusColor(s.status)}` }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginBottom: 4 }}>
              <strong>{s.title}</strong>
              <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 999, background: statusColor(s.status), color: "#fff", textTransform: "uppercase" }}>
                {s.status}
              </span>
            </div>
            <div className="afr-muted" style={{ fontSize: "0.82rem" }}>{s.owner_name} · {s.category || "—"}</div>
            {s.description && <p style={{ fontSize: "0.86rem", marginTop: 6 }}>{s.description.slice(0, 200)}</p>}
            {s.price_from != null && (
              <div style={{ fontSize: "0.86rem", marginTop: 6 }}>
                Dès {s.price_from} {s.currency}/{s.unit || "projet"}
              </div>
            )}
            <div style={{ display: "flex", gap: 6, marginTop: 10, flexWrap: "wrap" }}>
              {s.status !== "validated" && (
                <button className="afr-btn afr-btn--primary afr-btn--sm" onClick={() => onAction(s.id, "validate")} data-testid={`svc-validate-${s.id}`}>
                  <CheckCircle2 size={12} /> Valider
                </button>
              )}
              {s.status !== "rejected" && (
                <button className="afr-btn afr-btn--outline afr-btn--sm" onClick={() => onAction(s.id, "reject")} data-testid={`svc-reject-${s.id}`} style={{ color: "#B91C1C" }}>
                  <XCircle size={12} /> Rejeter
                </button>
              )}
              {s.status !== "pending" && (
                <button className="afr-btn afr-btn--ghost afr-btn--sm" onClick={() => onAction(s.id, "reset")} data-testid={`svc-reset-${s.id}`}>
                  <RotateCcw size={12} /> Remettre en attente
                </button>
              )}
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}

function CommissionsTab({ commissions, config, onReload }) {
  const [creditingId, setCreditingId] = useState(null);
  const [creditedIds, setCreditedIds] = useState(new Set());

  const creditWallet = async (commissionId) => {
    setCreditingId(commissionId);
    try {
      const { data } = await api.post(`/v1/payments/wallet/credit-from-commission/${commissionId}`);
      if (data.skipped) {
        toast.info(data.reason || "Aucun crédit appliqué");
      } else if (data.transaction) {
        toast.success(`Wallet crédité : +${(data.transaction.amount || 0).toFixed(0)} ${data.transaction.currency || ""}`);
        setCreditedIds((s) => new Set([...s, commissionId]));
        if (onReload) onReload();
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setCreditingId(null); }
  };

  const totals = commissions.reduce(
    (acc, c) => {
      acc.gross += c.gross_amount || 0;
      if (c.mode === "certified") acc.commission += c.commission_amount || 0;
      acc.freelance += c.freelance_amount || 0;
      return acc;
    },
    { gross: 0, commission: 0, freelance: 0 },
  );
  return (
    <div data-testid="tab-commissions-content">
      <div className="afr-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12, marginBottom: 16 }}>
        <Kpi Icon={DollarSign} label="Volume brut" value={fmtMoney(totals.gross)} accent="#0F2A42" />
        <Kpi Icon={Percent} label="Commission AFRICTAL" value={fmtMoney(totals.commission)} accent="#15803D" />
        <Kpi Icon={Users} label="Reversé freelances" value={fmtMoney(totals.freelance)} accent="#009640" />
      </div>

      {commissions.length === 0 ? (
        <div className="afr-empty" data-testid="commissions-empty">
          <div className="afr-empty__title">Aucune commission enregistrée</div>
        </div>
      ) : (
        <table className="afr-table" data-testid="commissions-table">
          <thead>
            <tr>
              <th>Date</th><th>Mission</th><th>Freelance</th><th>Mode</th>
              <th style={{ textAlign: "right" }}>Brut</th>
              <th style={{ textAlign: "right" }}>Taux</th>
              <th style={{ textAlign: "right" }}>Commission</th>
              <th style={{ textAlign: "right" }}>Reversé</th>
              <th style={{ textAlign: "center" }}>Wallet</th>
            </tr>
          </thead>
          <tbody>
            {commissions.map((c) => {
              const isCertified = c.mode === "certified";
              const isCredited = creditedIds.has(c.id) || c.wallet_credited === true;
              const isCrediting = creditingId === c.id;
              return (
              <tr key={c.id} data-testid={`commission-${c.id}`}>
                <td style={{ fontSize: "0.78rem" }}>{new Date(c.created_at).toLocaleDateString("fr-FR")}</td>
                <td>{c.mission_title}</td>
                <td>{c.freelance_name}</td>
                <td>
                  <span className="afr-badge" style={{
                    background: isCertified ? "rgba(21,128,61,0.12)" : "rgba(100,116,139,0.10)",
                    color: isCertified ? "#15803D" : "#64748b",
                  }}>
                    {isCertified ? "⭐ Certifiée" : "Externe"}
                  </span>
                </td>
                <td style={{ textAlign: "right" }}>{fmtMoney(c.gross_amount, c.currency)}</td>
                <td style={{ textAlign: "right" }}>{(c.commission_rate * 100).toFixed(1)}%</td>
                <td style={{ textAlign: "right", color: "#15803D", fontWeight: 700 }}>{fmtMoney(c.commission_amount, c.currency)}</td>
                <td style={{ textAlign: "right" }}>{fmtMoney(c.freelance_amount, c.currency)}</td>
                <td style={{ textAlign: "center" }}>
                  {!isCertified ? (
                    <span className="afr-muted" style={{ fontSize: "0.72rem" }}>—</span>
                  ) : isCredited ? (
                    <span className="afr-badge" data-testid={`commission-credited-${c.id}`} style={{
                      background: "rgba(21,128,61,0.14)", color: "#15803D", display: "inline-flex", alignItems: "center", gap: 4,
                    }}>
                      <CheckCircle2 size={12} /> Crédité
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => creditWallet(c.id)}
                      disabled={isCrediting}
                      className="afr-btn afr-btn--primary afr-btn--sm"
                      data-testid={`commission-credit-${c.id}`}
                      style={{ display: "inline-flex", alignItems: "center", gap: 4, whiteSpace: "nowrap" }}
                      title="Verser cette commission dans le wallet du freelance (idempotent)"
                    >
                      {isCrediting ? <Loader2 size={12} className="afr-spin" /> : <Zap size={12} />}
                      Créditer le wallet
                    </button>
                  )}
                </td>
              </tr>
            );})}
          </tbody>
        </table>
      )}
    </div>
  );
}

function Kpi({ Icon, label, value, accent, hint }) {
  return (
    <div className="afr-kpi" style={{ borderLeft: `3px solid ${accent}` }}>
      <div className="afr-kpi__icon" style={{ background: `color-mix(in oklab, ${accent} 14%, transparent)`, color: accent }}>
        <Icon size={18} />
      </div>
      <span className="afr-kpi__label">{label}</span>
      <span className="afr-kpi__value">{value}</span>
      {hint && <span className="afr-kpi__delta">{hint}</span>}
    </div>
  );
}

function Field({ label, testid, children }) {
  return (
    <label className="afr-card" style={{ padding: "0.9rem", display: "block" }} data-testid={testid}>
      <span className="afr-muted" style={{ fontSize: "0.78rem" }}>{label}</span>
      <div style={{ marginTop: "0.35rem" }}>
        <style>{`
          [data-testid='${testid}'] input,
          [data-testid='${testid}'] select {
            width: 100%; border: none; outline: none; background: transparent;
            font-family: 'Outfit', sans-serif; font-size: 0.95rem; color: var(--afr-secondary);
          }
        `}</style>
        {children}
      </div>
    </label>
  );
}

function statusColor(s) {
  return s === "validated" ? "#15803D" : s === "rejected" ? "#B91C1C" : "#B45309";
}

function fmtMoney(n, cur = "XOF") {
  if (!n && n !== 0) return "—";
  try { return `${new Intl.NumberFormat("fr-FR").format(n)} ${cur}`; } catch { return `${n} ${cur}`; }
}

function sum(obj) {
  return Object.values(obj || {}).reduce((a, b) => a + (b || 0), 0);
}
